<template>
  <div class="number-scope">
    <m-select
      v-if="!hideOp"
      :value="selectedType"
      :items="intervalTypes"
      :read-only="readOnly"
      :disabled="disabled"
      class="_op"
      @input="updateSelectedType"
    />
    <template
      v-if="!isEmpty"
    >
      <m-input-number
        :value="number"
        :read-only="readOnly"
        :disabled="disabled"
        :formatter="formatter"
        :parser="parser"
        nullable
        auto-focus
        class="_input"
        @input="updateNumber"
      />
    </template>
  </div>
</template>

<script>
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useScopeItem from '@/composables/filter/scope-item';
import { AND } from 'shared/api/query/constants';
import { EMPTY, NOT, NOT_EMPTY } from '@/lib/filter/scope-tree';
import { intervalType, propertyType } from 'shared/constants.json';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { numberFormatter, numberParser } from '@/lib/props/number';

export default {
  name: 'NumberScope',
  props: {
    value: {
      type: Object,
      required: true,
    },
    isNot: {
      type: Boolean,
      required: true,
    },
    numberFormat: {
      type: String,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideOp: {
      type: Boolean,
      default: false,
    },
    hideIsEmpty: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { userLang } = useLoggedInUser();
    const { operatorTypes: getOperatorTypes } = useScopeItem();
    const intervalTypes = getOperatorTypes(propertyType.number, { hideEmpty: props.hideIsEmpty });
    return { userLang, intervalTypes };
  },
  emits: ['input', 'update:value', 'change', 'change-not'],
  data() {
    return {
      number: null,
      selectedType: intervalType.eq,
      isEmpty: false,
    };
  },
  computed: {
    formatter() {
      return numberFormatter(this.numberFormat, this.userLang);
    },
    parser() {
      return numberParser(this.numberFormat, this.userLang);
    },
    rangeKey() {
      if ([intervalType.lt, intervalType.le].includes(this.selectedType)) {
        return 'maxType';
      }

      return 'minType';
    },
    numberKey() {
      if ([intervalType.lt, intervalType.le].includes(this.selectedType)) {
        return 'max';
      }
      return 'min';
    },
    numberRange() {
      return {
        [this.numberKey]: this.number,
        [this.rangeKey]: this.selectedType,
      };
    },
  },
  methods: {
    updateSelectedType(value) {
      this.emitNot(value);

      if ([EMPTY, NOT_EMPTY].includes(value)) {
        this.isEmpty = true;
        this.emitUpdate({ ...this.value, isEmpty: this.isEmpty });
        return;
      }
      this.isEmpty = false;
      this.selectedType = value;
      this.emitUpdate({ ...this.value, numberRange: this.numberRange, isEmpty: this.isEmpty });
    },
    updateNumber(value) {
      this.number = value;
      this.emitUpdate({ ...this.value, numberRange: this.numberRange });
    },
    setValues({ numberRange, isEmpty }) {
      this.isEmpty = isEmpty;
      if (isEmpty === true) {
        this.selectedType = this.isNot ? NOT_EMPTY : EMPTY;
        return;
      }
      if ([intervalType.lt, intervalType.le].includes(numberRange.maxType)) {
        this.selectedType = numberRange.maxType;
        this.number = numberRange.max;
        return;
      }

      this.number = numberRange.min;
      this.selectedType = numberRange.minType;
    },
    updateValues() {
      let isEmpty = false;
      if (this.value !== null && this.value.isEmpty !== undefined) {
        isEmpty = this.value.isEmpty;
      }
      this.isEmpty = isEmpty;

      if (this.value === null || isNullOrUndefined(this.value.numberRange)) {
        this.emitUpdate({ ...this.value, numberRange: this.numberRange, isEmpty: this.isEmpty });
        return;
      }
      this.setValues(this.value);
    },
    emitNot(selectedType) {
      if (selectedType !== NOT_EMPTY && this.isNot) {
        this.$emit('change-not', AND);
      }
      if (selectedType === NOT_EMPTY && !this.isNot) {
        this.$emit('change-not', NOT);
      }
    },
    emitUpdate(val) {
      this.$emit('input', val);
      this.$emit('update:value', val);
      this.$emit('change', val);
    },
  },
  watch: {
    value: {
      handler() {
        if (isNullOrUndefined(this.value)) {
          return;
        }
        this.updateValues();
      },
      deep: true,
    },
    isNot(newVal, oldVal) {
      if (newVal === oldVal) {
        return;
      }
      this.updateValues();
    },
  },
  created() {
    this.updateValues();
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .number-scope {
    display: flex;
    width: inherit;

    ._op {
      margin-right: .8rem;
    }

    ._input {
      width: inherit;
    }
  }
</style>
